<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { charityAvatarInitial, charityAvatarStyle } from '@/helpers/charityAvatar'

const props = withDefaults(
  defineProps<{
    charity: string
    /** Charity logo URL when supplied by the platform; letter fallback when absent or broken. */
    logoUrl?: string | null
    /** Optional brand colour (hex) for the letter fallback when no logo is shown. */
    brandColor?: string | null
    /** `lg` for detail views; default matches donation list rows. */
    size?: 'md' | 'lg'
  }>(),
  {
    size: 'md',
  }
)

const logoFailed = ref(false)

const resolvedLogoUrl = computed(() => props.logoUrl?.trim() ?? '')
const showLogo = computed(() => Boolean(resolvedLogoUrl.value) && !logoFailed.value)

const initial = computed(() => charityAvatarInitial(props.charity))
const letterStyle = computed(() => charityAvatarStyle(props.charity, props.brandColor))

watch(
  () => props.logoUrl,
  () => {
    logoFailed.value = false
  }
)

const onLogoError = () => {
  logoFailed.value = true
}
</script>

<template>
  <div
    class="sa-charity-avatar"
    :class="{
      'sa-charity-avatar--logo': showLogo,
      'sa-charity-avatar--letter': !showLogo,
      'sa-charity-avatar--lg': size === 'lg',
    }"
    :style="showLogo ? undefined : letterStyle"
    aria-hidden="true"
  >
    <img
      v-if="showLogo"
      class="sa-charity-avatar__logo"
      :src="resolvedLogoUrl"
      alt=""
      decoding="async"
      @error="onLogoError"
    />
    <span v-else class="sa-charity-avatar__initial">{{ initial }}</span>
  </div>
</template>
